import React, { useEffect, useRef } from 'react';
import { useSearchParams, useNavigate, useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { exchangeAuthCode } from '../../../store/shared/auth/authThunks';
import { selectAuth, selectIsAuthenticated } from '../../../store/shared/auth/authSelectors';
import Loading from '../../../components/common/Loading'; // Assuming you have a Loading component

const AuthCallback = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const params = useParams();
  let lang = params.lang; // may be undefined
  if (!lang) {
    lang = 'en'; // default to English if no lang param present
  }
  const dispatch = useDispatch();
  const { isLoading, error, userRole } = useSelector(selectAuth);
  const isAuthenticated = useSelector(selectIsAuthenticated);
  const exchangeAttempted = useRef(false); // <<< Add ref to track dispatch attempt

  const errorParam = searchParams.get('error');
  const errorDescription = searchParams.get('error_description');

  useEffect(() => {
    // --- Manually parse code from URL --- 
    const params = new URLSearchParams(window.location.search);
    const codeFromUrl = params.get('code');
    // --- End manual parse ---

    // --- Debug Logging Start ---
    console.log('[AuthCallback Effect 1] Running...');
    console.log('[AuthCallback Effect 1] Code from window.location:', codeFromUrl);
    console.log('[AuthCallback Effect 1] isLoading:', isLoading);
    console.log('[AuthCallback Effect 1] isAuthenticated:', isAuthenticated);
    console.log('[AuthCallback Effect 1] errorParam:', errorParam);
    // --- Debug Logging End ---

    if (errorParam) {
      console.error(`[AuthCallback Effect 1] Error from auth server: ${errorParam} - ${errorDescription}`);
      return; 
    }

    // Only attempt exchange if code exists, not loading, not authenticated, AND not already attempted
    if (codeFromUrl && !isLoading && !isAuthenticated && !exchangeAttempted.current) { 
      console.log('[AuthCallback Effect 1] Dispatching exchangeAuthCode with code:', codeFromUrl);
      exchangeAttempted.current = true; // <<< Mark as attempted *before* dispatching
      dispatch(exchangeAuthCode(codeFromUrl));
    } else {
      console.log('[AuthCallback Effect 1] Conditions not met for dispatching exchangeAuthCode.');
      if (!codeFromUrl) console.log('[AuthCallback Effect 1] Reason: No code found in window.location.');
      if (isLoading) console.log('[AuthCallback Effect 1] Reason: isLoading is true.');
      if (isAuthenticated) console.log('[AuthCallback Effect 1] Reason: isAuthenticated is true.');
      if (exchangeAttempted.current) console.log('[AuthCallback Effect 1] Reason: exchange already attempted.'); // <<< Log if already attempted
    }
    // Keep dependencies as they are - the ref prevents re-dispatch logic inside the effect
  }, [errorParam, errorDescription, dispatch, isLoading, isAuthenticated]);

  useEffect(() => {
    console.log('[AuthCallback Effect 2] Running. isAuthenticated:', isAuthenticated);
    if (isAuthenticated) {
      console.log('[AuthCallback Effect 2] Navigating to dashboard...');
      // Navigate to the correct dashboard once authenticated
      const dashboardPath = userRole === 'SuperAdmin' 
        ? `/${lang}/superadmin` 
        : `/${lang}/dashboard/home`;
      navigate(dashboardPath, { replace: true });
    }
  }, [isAuthenticated, userRole, navigate, lang]);

  // --- Render Logic Logging ---
  const renderCode = new URLSearchParams(window.location.search).get('code');
  console.log('[AuthCallback Render] State: isLoading:', isLoading, 'isAuthenticated:', isAuthenticated, 'error:', error, 'errorParam:', errorParam, 'code (manual):', renderCode);

  if (isLoading) {
    console.log('[AuthCallback Render] Rendering: Loading Spinner');
    return <Loading.Spinner fullScreen message="Authenticating..." />;
  }

  if (error || errorParam) {
    console.log('[AuthCallback Render] Rendering: Error Message');
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-red-600 bg-red-100 p-4 rounded border border-red-300">
          Authentication Failed: {error || errorDescription || 'An unknown error occurred.'}
          {/* You might want a button to retry or go back to login */}
        </div>
      </div>
    );
  }
  
  if (!renderCode && !errorParam && !isLoading && !isAuthenticated) { 
     console.log('[AuthCallback Render] Rendering: No Code Message');
     return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-yellow-600 bg-yellow-100 p-4 rounded border border-yellow-300">
           Invalid authentication callback. No code received. Please try logging in again.
           {/* Add a link/button to go back to login */}
        </div>
      </div>
     );
  }

  console.log('[AuthCallback Render] Rendering: Fallback Loading Spinner');
  return <Loading.Spinner fullScreen message="Processing authentication..." />; 
};

export default AuthCallback; 